import { HrisEmployeeFragment } from "@src/features/settings/hris/HrisEmployeeListContainer.generated";
import { useGetTraineesWithoutHrisEmployeeQuery } from "@src/features/settings/dialogs/LinkHrisEmployeeDialog.generated";
import { useToast } from "@src/lib/hooks/useToast";
import { useTracking } from "@src/features/app/hooks/useTracking";
import { GraphqlOperations } from "@src/types.generated";
import { launchConfetti } from "@src/lib/utils/confetti";
import { Button } from "@src/lib/ui/Button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@src/lib/ui/Command";
import { Popover, PopoverContent, PopoverTrigger } from "@src/lib/ui/Popover";
import { Label } from "@src/lib/ui/Label";
import Text from "@src/lib/ui/Text";
import { Switch } from "@src/lib/ui/Switch";
import { cn } from "@src/lib/utils/cn";
import ChevronDownIcon from "@src/lib/icons/18px/chevron-down";
import UserOutlineIcon from "@src/lib/icons/18px/user-outline";
import LinkIcon from "@src/lib/icons/18px/link";
import Fuse from "fuse.js";
import { FC, useMemo, useState, useRef } from "react";
import { gql } from "@apollo/client";
import { useLinkHrisEmployeeToOpusUserMutation } from "./LinkHrisEmployeeDialog.generated";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@src/lib/ui/Dialog";

const fuseConfig = {
  threshold: 0.4,
  keys: ["name", "phone_number"],
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  hrisEmployee: HrisEmployeeFragment;
};

const LinkHrisEmployeeDialog: FC<Props> = ({
  open,
  onOpenChange,
  hrisEmployee,
}) => {
  const { addErrorToast } = useToast();
  const { trackEvent } = useTracking();
  const [search, setSearch] = useState("");
  const [comboboxOpen, setComboboxOpen] = useState(false);
  const [showLinkedUsers, setShowLinkedUsers] = useState(false);
  const { data } = useGetTraineesWithoutHrisEmployeeQuery();
  const [selectedValue, setSelectedValue] = useState<string>(
    hrisEmployee.user?.id.toString() || "",
  );
  const [isSaving, setIsSaving] = useState(false);

  const initialValueRef = useRef<string>(
    hrisEmployee.user?.id.toString() || "",
  );

  const hasChanged = useMemo(() => {
    return selectedValue !== initialValueRef.current;
  }, [selectedValue]);

  const users = useMemo(() => {
    return data?.Employees?.objects || [];
  }, [data]);

  const filteredUsers = useMemo(() => {
    let _users = users;
    if (!showLinkedUsers) {
      _users = _users.filter((user) => user.hrisEmployee?.id === undefined);
    }
    if (search) {
      return new Fuse(_users, fuseConfig).search(search).map((e) => e.item);
    }
    return [..._users].sort((a, b) => {
      if (a?.name && b?.name) {
        return a.name.localeCompare(b.name);
      }
      return 0;
    });
  }, [users, search, showLinkedUsers]);

  const selectedUser = useMemo(() => {
    if (!selectedValue) return null;
    return (
      users.find((user) => user.id.toString() === selectedValue) ||
      (hrisEmployee.user?.id.toString() === selectedValue
        ? {
            id: hrisEmployee.user.id,
            name: hrisEmployee.user.name,
          }
        : null)
    );
  }, [selectedValue, users, hrisEmployee.user]);

  const selectedUserHasExistingLink = useMemo(() => {
    if (!selectedUser) return false;
    const userFromArray = users.find(
      (user) => user.id.toString() === selectedValue,
    );
    if (!userFromArray) return false;
    return (
      userFromArray.hrisEmployee?.id !== undefined &&
      userFromArray.hrisEmployee.id !== hrisEmployee.id
    );
  }, [selectedUser, selectedValue, users, hrisEmployee.id]);

  const [createHrisEmployeeLink] = useLinkHrisEmployeeToOpusUserMutation({
    refetchQueries: [
      GraphqlOperations.Query.HrisEmployees,
      GraphqlOperations.Query.HrisEmployeeSidebar,
    ],
    onCompleted: (data) => {
      setIsSaving(false);
      if (!data.createHrisEmployeeLink.success) {
        addErrorToast({
          hrisEmployeeId: hrisEmployee.id,
          opusEmployeeId: selectedValue ? parseInt(selectedValue) : undefined,
          callsite: "new_hris_employee_link",
        });
      } else {
        trackEvent("new_hris_employee_link", {
          hrisEmployeeId: hrisEmployee.id,
          opusEmployeeId: selectedValue ? parseInt(selectedValue) : undefined,
        });
        onOpenChange(false);
        launchConfetti();
      }
    },
    onError: (error) => {
      setIsSaving(false);
      addErrorToast({
        ...error,
        callsite: "new_hris_employee_link",
      });
    },
  });

  const handleSave = () => {
    if (!selectedValue) return;

    setIsSaving(true);
    createHrisEmployeeLink({
      variables: {
        hrisEmployeeId: hrisEmployee.id,
        traineeId: parseInt(selectedValue),
      },
    });
  };

  const handleSelect = (value: string) => {
    setSelectedValue(value);
    setComboboxOpen(false);
    setSearch("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit opus user link</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          <Label>Opus user</Label>
          <Popover open={comboboxOpen} onOpenChange={setComboboxOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={comboboxOpen}
                className="h-9 w-full justify-between font-normal"
              >
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  {(selectedUser || selectedValue) && (
                    <UserOutlineIcon className="size-4 shrink-0 text-muted-foreground" />
                  )}
                  <span className="truncate">
                    {selectedUser
                      ? selectedUser.name
                      : selectedValue && hrisEmployee.user?.name
                        ? hrisEmployee.user.name
                        : "Select a user..."}
                  </span>
                </div>
                <ChevronDownIcon className="ml-2 size-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className="z-[9999] w-[var(--radix-popover-trigger-width)] p-0"
              align="start"
              disablePortal
            >
              <Command shouldFilter={false}>
                <CommandInput
                  placeholder="Search for a user..."
                  value={search}
                  onValueChange={setSearch}
                />
                <CommandList className="max-h-[300px]">
                  <CommandEmpty>
                    {search ? "No users found" : "No users available"}
                  </CommandEmpty>
                  <CommandGroup>
                    <div className="px-2 py-1.5">
                      <div className="flex items-center justify-between gap-2">
                        <Label
                          htmlFor="show-linked-users"
                          className="cursor-pointer text-2xs-v4 font-normal"
                        >
                          Show Opus users already linked to an HRIS user
                        </Label>
                        <Switch
                          id="show-linked-users"
                          checked={showLinkedUsers}
                          onCheckedChange={setShowLinkedUsers}
                        />
                      </div>
                    </div>
                    {filteredUsers.map((user) => {
                      const value = user.id.toString();
                      const isSelected = selectedValue === value;
                      const isLinked = user.hrisEmployee?.id !== undefined;
                      return (
                        <CommandItem
                          key={user.id}
                          value={value}
                          onSelect={handleSelect}
                          className="cursor-pointer"
                        >
                          <div className="flex flex-1 items-center gap-2">
                            <UserOutlineIcon
                              className={cn(
                                "h-4 w-4 shrink-0",
                                isSelected
                                  ? "text-primary"
                                  : "text-muted-foreground",
                              )}
                            />
                            <Text
                              variant="compact-description"
                              className={cn(
                                "flex-1",
                                isSelected && "text-primary",
                              )}
                            >
                              {user.name}
                            </Text>
                            {isLinked && (
                              <div className="flex shrink-0 items-center gap-1">
                                <LinkIcon className="size-3 text-muted-foreground" />
                                <Text
                                  variant="fine-print-description"
                                  className="text-muted-foreground"
                                >
                                  Linked
                                </Text>
                              </div>
                            )}
                          </div>
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
        {selectedUserHasExistingLink && (
          <div className="rounded-md bg-gray-1 p-3">
            <Text variant="compact-description" multiline>
              This will replace the existing HRIS employee link for the selected
              opus user.
            </Text>
          </div>
        )}
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!selectedValue || isSaving || !hasChanged}
            loading={isSaving}
          >
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LinkHrisEmployeeDialog;

gql`
  query GetTraineesWithoutHrisEmployee {
    Employees: AdminEmployees {
      totalCount
      objects {
        ...EmployeeListItem
      }
    }
  }

  mutation LinkHrisEmployeeToOpusUser($traineeId: Int!, $hrisEmployeeId: Int!) {
    createHrisEmployeeLink(
      traineeId: $traineeId
      hrisEmployeeId: $hrisEmployeeId
    ) {
      success
    }
  }
`;
